import { lazy } from "react";
import { Navigate } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import RequireAuth from "../components/feature/RequireAuth";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";
import ComingSoon from "../pages/ComingSoon";
import AuctionsPage from "../pages/auctions/page";
import AuctionDetailPage from "../pages/auctions/detail/page";
import CoursesPage from "../pages/courses/page";
import CourseDetailPage from "../pages/courses/detail/page";
import LecturesPage from "../pages/lectures/page";
import LectureDetailPage from "../pages/lectures/detail/page";
import ConsultationPage from "../pages/consultation/page";
import ReportsPage from "../pages/reports/page";
import BidCalculatorPage from "../pages/bid-calculator/page";
import BidCalculatorGuidePage from "../pages/bid-calculator-guide/page";
import BidCalculatorPRDPage from "../pages/bid-calculator-prd/page";
import ProductPRDPage from "../pages/prd/page";
import ComparePage from "../pages/compare/page";
import LoginPage from "../pages/login/page";
import RegisterPage from "../pages/register/page";
import MyPage from "../pages/mypage/page";
import CommunityPage from "../pages/community/page";
import AdminPage from "../pages/admin/page";
import BlogPage from "../pages/blog/page";
import BlogDetailPage from "../pages/blog/detail/page";
import AnalysisPage from "../pages/analysis/page";
import InvestmentSimulatorPage from "../pages/investment-simulator/page";
import MaterialsPage from "../pages/materials/page";
import AwardedInsightsPage from "../pages/awarded-insights/page";
import AwardedInsightDetailPage from "../pages/awarded-insights/detail/page";
import PrivacyPage from "../pages/privacy/page";

// auction-biz는 tesseract.js·pdfjs-dist(OCR/PDF 파싱, 수 MB)를 물고 있어
// 다른 모든 페이지의 로딩까지 무거워졌다 — lazy로 방문 시에만 받게 분리.
const AuctionBizHomePage = lazy(() => import("../pages/auction-biz/home/page"));
const AuctionBizGuidePage = lazy(() => import("../pages/auction-biz/guide/page"));
const AuctionBizCalculatorPage = lazy(() => import("../pages/auction-biz/calculator/page"));
const AuctionBizBatchPage = lazy(() => import("../pages/auction-biz/batch/page"));
const AuctionBizRentalPage = lazy(() => import("../pages/auction-biz/rental/page"));
const AuctionBizSeedPage = lazy(() => import("../pages/auction-biz/seed/page"));

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/auctions",
    element: <AuctionsPage />,
  },
  {
    path: "/auctions/:id",
    element: <AuctionDetailPage />,
  },
  {
    path: "/courses",
    element: <CoursesPage />,
  },
  {
    path: "/courses/:id",
    element: <CourseDetailPage />,
  },
  {
    path: "/lectures",
    element: <LecturesPage />,
  },
  {
    path: "/lectures/:id",
    element: <LectureDetailPage />,
  },
  {
    path: "/materials",
    element: <MaterialsPage />,
  },
  {
    path: "/consultation",
    element: <ConsultationPage />,
  },
  {
    path: "/reports",
    element: <ReportsPage />,
  },
  {
    path: "/bid-calculator",
    element: <BidCalculatorPage />,
  },
  {
    path: "/bid-calculator-guide",
    element: <BidCalculatorGuidePage />,
  },
  {
    path: "/bid-calculator-prd",
    element: <BidCalculatorPRDPage />,
  },
  {
    path: "/prd",
    element: <ProductPRDPage />,
  },
  {
    path: "/investment-simulator",
    element: <InvestmentSimulatorPage />,
  },
  {
    path: "/compare",
    element: <ComparePage />,
  },
  {
    path: "/community",
    element: <CommunityPage />,
  },
  {
    path: "/community/:section",
    element: <CommunityPage />,
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
  },
  {
    path: "/mypage",
    element: (
      <RequireAuth>
        <MyPage />
      </RequireAuth>
    ),
  },
  {
    path: "/mypage/:section",
    element: (
      <RequireAuth>
        <MyPage />
      </RequireAuth>
    ),
  },
  {
    path: "/admin",
    element: (
      <RequireAuth requireAdmin>
        <AdminPage />
      </RequireAuth>
    ),
  },
  {
    path: "/admin/:section",
    element: (
      <RequireAuth requireAdmin>
        <AdminPage />
      </RequireAuth>
    ),
  },
  {
    path: "/about",
    element: <ComingSoon title="강스터디 소개" description="브랜드 소개, 강사 소개 페이지가 곧 업데이트됩니다." />,
  },
  {
    path: "/about/:section",
    element: <ComingSoon title="강스터디 소개" description="소개 페이지가 곧 업데이트됩니다." />,
  },
  {
    path: "/reviews",
    element: <ComingSoon title="수강 후기" description="전체 수강 후기 페이지가 곧 업데이트됩니다." />,
  },
  {
    path: "/faq",
    element: <ComingSoon title="자주 묻는 질문" description="FAQ 페이지가 곧 업데이트됩니다." />,
  },
  {
    path: "/contact",
    element: <ComingSoon title="문의하기" description="문의하기 페이지가 곧 업데이트됩니다." />,
  },
  {
    path: "/location",
    element: <ComingSoon title="오시는 길" description="오시는 길 페이지가 곧 업데이트됩니다." />,
  },
  {
    path: "/privacy",
    element: <PrivacyPage />,
  },
  {
    path: "/terms",
    element: <ComingSoon title="이용약관" description="이용약관 페이지가 곧 업데이트됩니다." />,
  },
  {
    path: "/blog",
    element: <BlogPage />,
  },
  {
    path: "/blog/:slug",
    element: <BlogDetailPage />,
  },
  {
    path: "/analysis",
    element: <AnalysisPage />,
  },
  {
    path: "/awarded-insights",
    element: <AwardedInsightsPage />,
  },
  {
    path: "/awarded-insights/:id",
    element: <AwardedInsightDetailPage />,
  },
  {
    path: "/auction-biz",
    element: <AuctionBizHomePage />,
  },
  {
    path: "/auction-biz/guide",
    element: <AuctionBizGuidePage />,
  },
  {
    path: "/auction-biz/calculator",
    element: <AuctionBizCalculatorPage />,
  },
  {
    path: "/auction-biz/batch",
    element: <AuctionBizBatchPage />,
  },
  {
    path: "/auction-biz/rental",
    element: <AuctionBizRentalPage />,
  },
  {
    path: "/auction-biz/seed",
    element: <AuctionBizSeedPage />,
  },
  // 원본 Readdy 프로젝트의 최상위 경로(/calculator 등)로 들어오는 사람이
  // 반복돼서(북마크·기억) 리다이렉트를 걸어둔다 — 매번 안내하는 대신.
  {
    path: "/calculator",
    element: <Navigate to="/auction-biz/calculator" replace />,
  },
  {
    path: "/guide",
    element: <Navigate to="/auction-biz/guide" replace />,
  },
  {
    path: "/batch",
    element: <Navigate to="/auction-biz/batch" replace />,
  },
  {
    path: "/rental",
    element: <Navigate to="/auction-biz/rental" replace />,
  },
  {
    path: "/seed",
    element: <Navigate to="/auction-biz/seed" replace />,
  },
  {
    path: "/auction",
    element: <Navigate to="/auctions" replace />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
